import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// SPEC-meet-ux-007 / GAP-MEETUX-005 follow-up (2026-06-18).
// Hand-rolled minimal Deal API client (no swagger codegen yet). Sufficient for
// Step 6 of the baseline user-flow ("IS persona adds account and opportunity
// without CRM sync"). Mirrors the Bearer + x-tenant-id pattern used by the
// auto-generated services in shared/apis/*. Backend routes live under the
// BusinessAccounts host (see Deal.cs / DealController.cs).
//
// Replace with the full swagger-generated service when /api/v1/Deal swagger is
// regenerated and dropped under shared/apis/deal/.

export interface DealCreateBody {
    id?: string;
    name: string;
    code?: string | null;
    businessAccountId: string;
    ownerUserId: string;
    productId?: string | null;
    buildId?: string | null;
    crmExternalId?: string | null;
    crmProvider?: string | null;
    stage: string;                // DealStages: pre-qualified / virtual-qualified / in-person-scheduled / decision-mode / stalled / closed-won / closed-lost
    value?: number | null;
    currency?: string;
    expectedCloseDate?: string | null;
    isActive?: boolean;
}

export interface DealCreateCommand {
    dealDetails: DealCreateBody;
}

export interface DealDto extends DealCreateBody {
    id: string;
    tenantId: string;
    createdDate?: string;
    modifiedDate?: string;
    diaScore?: number | null;
    atRiskFlag?: boolean;
    // GAP-BACKEND-247 (Phase 5) / SPEC-meet-ux-018 AC-1 — read-only; changes
    // only through addContact/updateContactRole/removeContact below.
    // camelCase wire shape per DealDto.cs:51 (DealContactDto).
    contacts?: Array<{ contactId: string; role?: string | null }>;
}

interface WebApiResponse<T> {
    isSuccess: boolean;
    message?: string;
    result?: T;
    erros?: Array<{ code: number; message: string; details?: string }>;
}

export interface DealContactAddBody {
    contactId: string;
    role?: string | null;
}

export interface DealContactRoleBody {
    role?: string | null;
}

/**
 * SPEC-web-planner-008 AC-21 / AC-23 — true only for the deal read's proven absence. Two backend shapes:
 * - 404 (SPEC-backend-065, GAP-BACKEND-442): a WebApiResponse with `erros[].errorCode` 404 or the message
 *   "Deal not found". A 404 with neither (an ingress or routing 404) proves nothing about the deal.
 * - 400 `{"101": ["Deal not found"]}` (the backend before SPEC-backend-065): only the text tells it from an
 *   exception, which is also code 101. Remove this branch once every stage runs SPEC-backend-065 (GAP-BACKEND-442).
 */
export function isDealNotFound(err: any): boolean {
    if (err?.status !== 400 && err?.status !== 404) { return false; }
    let body = err?.error;
    if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch { /* not JSON: matched as text below */ }
    }
    if (err.status === 404 && Array.isArray(body?.erros) && body.erros.some((e: any) => Number(e?.errorCode) === 404)) {
        return true;
    }
    let text = '';
    if (typeof body === 'string') {
        text = body;
    } else {
        try { text = JSON.stringify(body ?? ''); } catch { text = ''; }
    }
    return text.includes('Deal not found');
}

@Injectable({ providedIn: 'root' })
export class DealService {
    private readonly basePath: string = environment.api.businessAccounts;

    constructor(private http: HttpClient) {}

    private headers(tenantId: string, accessToken?: string): HttpHeaders {
        let h = new HttpHeaders().set('x-tenant-id', tenantId);
        if (accessToken) h = h.set('Authorization', `Bearer ${accessToken}`);
        return h;
    }

    /**
     * SPEC-web-planner-008 AC-21 — one deal by id: GET api/v1/Deal/{id}, read under the caller's tenant filter. A deal
     * that does not exist, or belongs to another client, answers 404 (SPEC-backend-065), or 400 with
     * `{"101": ["Deal not found"]}` on a backend before it (see
     * {@link isDealNotFound}).
     */
    get(tenantId: string, dealId: string, accessToken?: string): Observable<WebApiResponse<DealDto>> {
        return this.http.get<WebApiResponse<DealDto>>(
            `${this.basePath}/api/v1/Deal/${encodeURIComponent(dealId)}`,
            { headers: this.headers(tenantId, accessToken) },
        );
    }

    create(tenantId: string, body: DealCreateBody, accessToken?: string): Observable<WebApiResponse<DealDto>> {
        const command: DealCreateCommand = { dealDetails: body };
        return this.http.post<WebApiResponse<DealDto>>(
            `${this.basePath}/api/v1/Deal`,
            command,
            { headers: this.headers(tenantId, accessToken) }
        );
    }

    listByAccount(tenantId: string, accountId: string, accessToken?: string): Observable<WebApiResponse<DealDto[]>> {
        // GAP-WEBPLANNER-055 — the param MUST be named businessAccountId:
        // ASP.NET binds [FromQuery] DealsPagedListQuery by property name and
        // silently drops unknown params. The old `accountId=` matched no
        // property, so the backend skipped the filter and returned the
        // tenant-wide first page. pageSize=100 is the backend MaxPageSize;
        // omitting it defaults to 20 and silently truncates larger accounts.
        const url = `${this.basePath}/api/v1/Deal?businessAccountId=${encodeURIComponent(accountId)}&pageSize=100`;
        return this.http.get<WebApiResponse<DealDto[]>>(url, { headers: this.headers(tenantId, accessToken) });
    }

    /**
     * SESSION-webplanner-005 / GAP-WEBPLANNER-005 — list all deals for the
     * caller's tenant (no account scope). Used by /opportunities. The backend
     * `GET /api/v1/Deal` works without `accountId` and returns the full paged
     * set; the wrapper still returns the plain list shape per WebApiResponse.
     */
    list(tenantId: string, accessToken?: string, ownerId?: string, pageSize?: number): Observable<WebApiResponse<DealDto[]>> {
        const params: string[] = [];
        // The backend filter is OwnerUserId (DealsPagedListQuery); ownerId did not bind.
        if (ownerId) { params.push(`OwnerUserId=${encodeURIComponent(ownerId)}`); }
        // SPEC-web-planner-019 AC-36b — without it the backend default page is 20 deals.
        if (pageSize) { params.push(`pageSize=${pageSize}`); }
        const qs = params.length ? `?${params.join('&')}` : '';
        const url = `${this.basePath}/api/v1/Deal${qs}`;
        return this.http.get<WebApiResponse<DealDto[]>>(url, { headers: this.headers(tenantId, accessToken) });
    }

    /**
     * SPEC-web-planner-013 AC-46 — the person's own deals (My Deals). The
     * backend filter is `OwnerUserId` (DealsPagedListQuery). One page of up
     * to 100 (the server max); the caller says when there are more.
     */
    listMine(tenantId: string, ownerUserId: string, accessToken?: string): Observable<WebApiResponse<DealDto[]>> {
        const url = `${this.basePath}/api/v1/Deal`;
        return this.http.get<WebApiResponse<DealDto[]>>(url, {
            headers: this.headers(tenantId, accessToken),
            params: { OwnerUserId: ownerUserId, pageSize: '100' },
        });
    }

    // GAP-BACKEND-247 (Phase 5) / SPEC-meet-ux-018 AC-1, AC-9, AC-10 — per-opportunity
    // committee. Subset rule (contact must already be on the deal's account) is
    // enforced server-side; failures arrive as an HttpErrorResponse (400).

    addContact(tenantId: string, dealId: string, body: DealContactAddBody, accessToken?: string): Observable<WebApiResponse<DealDto>> {
        return this.http.post<WebApiResponse<DealDto>>(
            `${this.basePath}/api/v1/Deal/${dealId}/Contacts`,
            body,
            { headers: this.headers(tenantId, accessToken) }
        );
    }

    updateContactRole(tenantId: string, dealId: string, contactId: string, body: DealContactRoleBody, accessToken?: string): Observable<WebApiResponse<DealDto>> {
        return this.http.put<WebApiResponse<DealDto>>(
            `${this.basePath}/api/v1/Deal/${dealId}/Contacts/${contactId}`,
            body,
            { headers: this.headers(tenantId, accessToken) }
        );
    }

    removeContact(tenantId: string, dealId: string, contactId: string, accessToken?: string): Observable<WebApiResponse<DealDto>> {
        return this.http.delete<WebApiResponse<DealDto>>(
            `${this.basePath}/api/v1/Deal/${dealId}/Contacts/${contactId}`,
            { headers: this.headers(tenantId, accessToken) }
        );
    }
}

// Source of truth: awvent-backend/src/Services/Shared/Awvent.Shared.Enums/Workspace/Deal.cs
// (DealStage enum, wire tokens via EnumMemberJsonConverter<DealStage>). This array is a
// defensive FE mirror, NOT linked to the backend enum at build time — if the backend adds
// a DealStage member, it stays unreachable from any dropdown built off this list until this
// array is updated by hand.
export const DealStageOptions: Array<{ value: string; label: string }> = [
    { value: 'pre-qualified',        label: 'Pre-qualified' },
    { value: 'virtual-qualified',    label: 'Virtual qualified' },
    { value: 'in-person-scheduled',  label: 'In-person scheduled' },
    { value: 'decision-mode',        label: 'Decision mode' },
    { value: 'stalled',              label: 'Stalled' },
    { value: 'closed-won',           label: 'Closed-won' },
    { value: 'closed-lost',          label: 'Closed-lost' },
];
